import React, { useState } from 'react';
import { Button } from './ui/button';
import { Gift, Copy, CheckCircle, Mail, Share2 } from 'lucide-react';

/**
 * Referral-Card: Empfehlungs-Block für /danke und Mitarbeiter-Dashboard.
 * Generiert einen sharable Link `/register?ref=<base64(email)>`.
 * Manuelle Gutschrift: Pro erfolgreicher Empfehlung 50€ Digistore24-Gutschein für
 * Werber UND Geworbenen.
 */
export const ReferralCard = ({ refEmail = '', variant = 'default' }) => {
  const [copied, setCopied] = useState(false);

  const refCode = refEmail
    ? typeof window !== 'undefined' && window.btoa
      ? window.btoa(refEmail).replace(/=+$/, '')
      : encodeURIComponent(refEmail)
    : '';
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const referralLink = `${siteUrl}/register${refCode ? `?ref=${refCode}` : ''}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      const ta = document.createElement('textarea');
      ta.value = referralLink;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent('SmartVolt Academy: 50€ Gutschein für deinen Betrieb');
    const body = encodeURIComponent(
      `Hi,\n\nich nutze die SmartVolt Academy für rechtssichere DGUV-Unterweisungen meines Teams – läuft echt gut.\n\nWenn du dich über meinen Link anmeldest, bekommen wir beide einen 50€ Digistore24-Gutschein:\n\n${referralLink}\n\nViele Grüße`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const compact = variant === 'compact';

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-yellow-500/40 bg-gradient-to-br from-yellow-500/15 via-yellow-500/5 to-transparent ${compact ? 'p-5' : 'p-6'} text-left`}
      data-testid="referral-card"
    >
      <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-yellow-500/10 blur-2xl" aria-hidden />
      <div className="relative">
        <div className="flex items-start gap-3 mb-3">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-yellow-500 text-black flex items-center justify-center">
            <Gift className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="text-white font-bold text-lg leading-snug">
              Empfehlung wert <span className="text-yellow-400">50&nbsp;€</span>
            </h3>
            <p className="text-sm text-gray-300 mt-0.5">
              Empfiehl SmartVolt Academy weiter – ihr bekommt beide einen 50&nbsp;€ Digistore24-Gutschein, sobald der geworbene Betrieb ein Premium-Paket bucht.
            </p>
          </div>
        </div>

        {!compact && (
          <ul className="text-xs text-gray-400 space-y-1 mb-4 pl-13 ml-1">
            <li className="flex items-start gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" /> Du bekommst <strong className="text-yellow-400">50&nbsp;€</strong> auf dein nächstes Add-On.</li>
            <li className="flex items-start gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" /> Dein Freund/Kollege bekommt <strong className="text-yellow-400">50&nbsp;€</strong> Startguthaben.</li>
            <li className="flex items-start gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" /> Keine Begrenzung – empfiehl so viele Betriebe wie du willst.</li>
          </ul>
        )}

        <div className="flex items-stretch gap-2 mt-2">
          <div className="flex-1 min-w-0 bg-black/40 border border-gray-800 rounded-lg px-3 py-2.5 text-xs text-gray-300 truncate font-mono" data-testid="referral-link">
            {referralLink}
          </div>
          <Button
            onClick={handleCopy}
            className="bg-yellow-500 text-black hover:bg-yellow-400 font-medium shrink-0"
            data-testid="referral-copy-btn"
          >
            {copied ? <CheckCircle className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
            {copied ? 'Kopiert' : 'Link kopieren'}
          </Button>
        </div>

        {!compact && (
          <div className="flex gap-2 mt-3">
            <Button
              onClick={handleEmailShare}
              variant="outline"
              className="flex-1 border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10"
              data-testid="referral-email-btn"
            >
              <Mail className="w-4 h-4 mr-1.5" /> Per E-Mail teilen
            </Button>
            <Button
              onClick={async () => {
                if (navigator.share) {
                  try {
                    await navigator.share({
                      title: 'SmartVolt Academy',
                      text: '50€ Gutschein für deinen Betrieb:',
                      url: referralLink });
                  } catch (e) { /* user cancelled */ }
                } else {
                  handleCopy();
                }
              }}
              variant="outline"
              className="flex-1 border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10"
              data-testid="referral-share-btn"
            >
              <Share2 className="w-4 h-4 mr-1.5" /> Teilen
            </Button>
          </div>
        )}

        <p className="text-[10px] text-gray-500 mt-3">
          Gutschein wird nach erfolgreicher Erstbuchung des geworbenen Betriebs per E-Mail zugestellt. Keine Auszahlung in bar.
        </p>
      </div>
    </div>
  );
};

export default ReferralCard;
